import { SafeUrl } from '@angular/platform-browser';
import {
  CustomerServiceNoteDetail,
  DeactivationReason,
  Driver,
  DriverAnnualRideExecutionData,
  DriverBank,
  DriverBankTransaction,
  DriverBankTransactionReason,
  DriverSuspensionDetail,
  Person,
  Trip
} from '@uzurv/clara';
import { isNullOrUndefined } from '@uzurv/core';
import {
  Address,
  MvrDetails,
  RandomScreeningDriverSelection,
  RandomScreeningDriverSelectionInfo,
  RandomScreeningYearlyProgress,
  RefusalReason,
  RuleValue,
  Screening,
  SubstanceScreening,
  TaxDocument,
  Vehicle
} from '@uzurv/dots';
import { cloneDeep, findIndex } from 'lodash';

import * as moment from 'moment-timezone';
import * as DriverActions from './driver.actions';
import { createReducer, on } from '@ngrx/store';

export interface DriverState {
  driverSearchString: string;
  driverSearchResults: Array<Driver>;
  driverSearchPageNumber: number;
  driverSearchPageSize: number;
  driverSearchLoading: boolean;
  driver: Driver;
  vehicles: Array<Vehicle>;
  driverProfilePictureUrl: SafeUrl;
  driverBank: DriverBank;
  driverBankTransactionCreditReasons: Array<DriverBankTransactionReason>;
  driverBankTransactionDebitReasons: Array<DriverBankTransactionReason>;
  customerServiceNotes: Array<CustomerServiceNoteDetail>;
  customerServiceNotesPageNumber: number;
  customerServiceNotesPageSize: number;
  bankTransactions: Array<DriverBankTransaction>;
  bankTransactionsStartDate: Date;
  bankTransactionsEndDate: Date;
  bankTransactionsPageNumber: number;
  bankTransactionsPageSize: number;
  reservations: Array<Trip>;
  reservationsPageIndex: number;
  reservationsPageSize: number;
  driverScreenings: Array<Screening>;
  driverScreeningsPageIndex: number;
  driverScreeningsPageSize: number;
  expandedScreening: Screening;
  substanceScreening: SubstanceScreening;
  mvrOrderDetails: MvrDetails;
  driverAddress: Address;
  driverSuspensions: Array<DriverSuspensionDetail>;
  lastSuspensionDate: Date;
  deactivationReasons: Array<DeactivationReason>;
  viewReferrer: boolean;
  editReferrer: boolean;
  referrerSearchResults: Array<Person>;
  referrerSearchPageNumber: number;
  referrerSearchPageSize: number;
  selectedReferrer: Person;
  referrer: Person;
  randomScreeningYearlyProgress: RandomScreeningYearlyProgress;
  randomScreeningDriverSelectionInfos: Array<RandomScreeningDriverSelectionInfo>;
  randomScreeningDriverSelectionInfosPageIndex: number;
  randomScreeningDriverSelectionInfosPageSize: number;
  randomScreeningDriverSelection: RandomScreeningDriverSelection;
  refusalReasons: Array<RefusalReason>;
  randomScreeningConfigurationValues: Array<RuleValue>;
  currentlyEditedRandomScreeningConfigurationValue: RuleValue;
  driverAnnualRideExecutionData: Array<DriverAnnualRideExecutionData>;
  driverTaxDocuments: Array<TaxDocument>;
  selectedTabIndex: number;
}

export const initialState: DriverState = {
  driverSearchString: '',
  driverSearchResults: null,
  driverSearchPageNumber: 0,
  driverSearchPageSize: 20,
  driverSearchLoading: false,
  driver: null,
  vehicles: null,
  driverProfilePictureUrl: null,
  driverBank: null,
  driverBankTransactionCreditReasons: null,
  driverBankTransactionDebitReasons: null,
  customerServiceNotes: null,
  customerServiceNotesPageNumber: 0,
  customerServiceNotesPageSize: 10,
  bankTransactions: null,
  bankTransactionsStartDate: moment().subtract(1, 'month').toDate(),
  bankTransactionsEndDate: new Date(),
  bankTransactionsPageNumber: 0,
  bankTransactionsPageSize: 10,
  driverScreenings: [],
  driverScreeningsPageIndex: 0,
  driverScreeningsPageSize: 10,
  expandedScreening: null,
  substanceScreening: null,
  mvrOrderDetails: null,
  reservations: null,
  reservationsPageIndex: 0,
  reservationsPageSize: 10,
  driverAddress: null,
  driverSuspensions: null,
  lastSuspensionDate: null,
  deactivationReasons: null,
  viewReferrer: false,
  editReferrer: false,
  referrerSearchResults: null,
  referrerSearchPageNumber: 0,
  referrerSearchPageSize: 15,
  selectedReferrer: null,
  referrer: null,
  randomScreeningYearlyProgress: null,
  randomScreeningDriverSelectionInfos: [],
  randomScreeningDriverSelectionInfosPageIndex: 0,
  randomScreeningDriverSelectionInfosPageSize: 1000,
  randomScreeningDriverSelection: null,
  refusalReasons: [],
  randomScreeningConfigurationValues: [],
  currentlyEditedRandomScreeningConfigurationValue: null,
  driverAnnualRideExecutionData: [],
  driverTaxDocuments: [],
  selectedTabIndex: 0
  // disConfirmDriverAddressDialog: false
};

export const driverReducer = createReducer(
  initialState,
  on(DriverActions.EditDriverProfileSuccess, (state, action) => {
    const driver = cloneDeep(state.driver);
    driver.firstName = action.driver.firstName;
    driver.middleName = action.driver.middleName;
    driver.hasMiddleName = action.driver.hasMiddleName;
    driver.lastName = action.driver.lastName;

    if (action.displayAddressValidationDialog === false) {
      const driverAddress = isNullOrUndefined(state.driverAddress) ? new Address() : cloneDeep(state.driverAddress);
      driverAddress.streetAddress1 = action.address.streetAddress1;
      driverAddress.streetAddress2 = action.address.streetAddress2;
      driverAddress.city = action.address.city;
      driverAddress.stateCode = action.address.stateCode;
      driverAddress.zipCode = action.address.zipCode;
      driverAddress.driverId = driver.dotsDriverIdentifier;
      driverAddress.verified = action.address.verified;

      return {
        ...state,
        driver: driver,
        driverAddress: driverAddress
      };
    }

    return {
      ...state,
      driver: driver
    };
  }),

  on(DriverActions.RestoreState, (state, action) => {
    return {
      ...action.state
    };
  }),

  on(DriverActions.DriverSearch, (state, action) => {
    return {
      ...state,
      driverSearchString: action.searchString
    };
  }),

  on(DriverActions.ShowMoreDriverSearchResults, (state, action) => {
    return {
      ...state,
      driverSearchPageSize: (state.driverSearchPageSize += 10)
    };
  }),

  on(DriverActions.DriverSearchSuccess, (state, action) => {
    return {
      ...state,
      driverSearchResults: action.drivers
    };
  }),

  on(DriverActions.DriverSearchFail, (state, action) => {
    return {
      ...state
    };
  }),

  on(DriverActions.ClearDriverSearch, (state, action) => {
    return {
      ...initialState
    };
  }),

  on(DriverActions.DriverSelected, (state, action) => {
    return {
      ...state,
      driver: action.driver,
      vehicles: initialState.vehicles,
      driverProfilePictureUrl: initialState.driverProfilePictureUrl,
      driverBank: initialState.driverBank,
      customerServiceNotes: initialState.customerServiceNotes,
      bankTransactions: initialState.bankTransactions,
      driverScreenings: initialState.driverScreenings,
      driverScreeningsPageIndex: initialState.driverScreeningsPageIndex,
      reservations: initialState.reservations,
      mvrOrderDetails: initialState.mvrOrderDetails,
      substanceScreening: initialState.substanceScreening,
      expandedScreening: initialState.expandedScreening,
      driverAddress: initialState.driverAddress,
      driverSuspensions: initialState.driverSuspensions,
      lastSuspensionDate: initialState.lastSuspensionDate,
      driverAnnualRideExecutionData: initialState.driverAnnualRideExecutionData,
      driverTaxDocuments: initialState.driverTaxDocuments
    };
  }),

  on(DriverActions.GetDriverSuccess, (state, action) => {
    return {
      ...state,
      driver: action.driver,
      bankTransactionsStartDate: moment().subtract(1, 'month').toDate(),
      bankTransactionsEndDate: new Date()
    };
  }),

  on(DriverActions.GetVehiclesSuccess, (state, action) => {
    return {
      ...state,
      vehicles: action.vehicles
    };
  }),

  on(DriverActions.GetDriverProfilePictureSuccess, (state, action) => {
    return {
      ...state,
      driverProfilePictureUrl: action.safeUrl
    };
  }),

  on(DriverActions.GetDriverBankSuccess, (state, action) => {
    return {
      ...state,
      driverBank: action.driverBank
    };
  }),

  on(DriverActions.GetDriverBankTransactionCreditReasonsSuccess, (state, action) => {
    return {
      ...state,
      driverBankTransactionCreditReasons: action.driverBankTransactionCreditReasons
    };
  }),

  on(DriverActions.GetDriverBankTransactionDebitReasonsSuccess, (state, action) => {
    return {
      ...state,
      driverBankTransactionDebitReasons: action.driverBankTransactionDebitReasons
    };
  }),

  on(DriverActions.GetCustomerServiceNotesSuccess, (state, action) => {
    return {
      ...state,
      customerServiceNotes: action.notes
    };
  }),

  on(DriverActions.AddCustomerServiceNotesPage, (state, action) => {
    return {
      ...state,
      customerServiceNotesPageSize: state.customerServiceNotesPageSize + 10
    };
  }),

  on(DriverActions.GetDriverBankTransactionsSuccess, (state, action) => {
    return {
      ...state,
      bankTransactions: action.bankTransactions
    };
  }),

  on(DriverActions.DriverBankTransactionsPagination, (state, action) => {
    return {
      ...state,
      bankTransactionsPageNumber: action.pageEvent.pageIndex,
      bankTransactionsPageSize: action.pageEvent.pageSize
    };
  }),

  on(DriverActions.GetDriverScreeningsSuccess, (state, action) => {
    return {
      ...state,
      driverScreenings: action.screenings
    };
  }),

  on(DriverActions.DriverScreeningsPagination, (state, action) => {
    return {
      ...state,
      driverScreeningsPageIndex: action.pageEvent.pageIndex,
      driverScreeningsPageSize: action.pageEvent.pageSize
    };
  }),

  on(DriverActions.ExpandScreening, (state, action) => {
    state.driverScreenings.forEach((screening: Screening) => {
      if (action.screening.id === screening.id) {
        action.screening = screening;
        return;
      }
    });

    return {
      ...state,
      mvrOrderDetails: null,
      expandedScreening: action.screening === state.expandedScreening ? null : action.screening
    };
  }),

  on(DriverActions.GetSubstanceScreeningSuccess, (state, action) => {
    return {
      ...state,
      mvrOrderDetails: null,
      substanceScreening: action.substanceScreening
    };
  }),

  on(DriverActions.ClearSubstanceScreening, (state, action) => {
    return {
      ...state,
      substanceScreening: null
    };
  }),

  on(DriverActions.GetMVRScreeningDetails, (state, action) => {
    return {
      ...state,
      substanceScreening: null
    };
  }),

  on(DriverActions.GetMVRScreeningDetailsSuccess, (state, action) => {
    return {
      ...state,
      substanceScreening: null,
      mvrOrderDetails: action.mvrOrderDetails
    };
  }),

  on(DriverActions.GetDriverReservationsSuccess, (state, action) => {
    return {
      ...state,

      reservations: action.reservations
    };
  }),

  on(DriverActions.DriverBankTransactionFiltersChanged, (state, action) => {
    return {
      ...state,
      bankTransactionsStartDate: action.filters.find(filter => filter.paramName === 'startDate').value,
      bankTransactionsEndDate: action.filters.find(filter => filter.paramName === 'endDate').value,
      bankTransactionsPageNumber: 0
    };
  }),

  on(DriverActions.ReservationsPagination, (state, action) => {
    return {
      ...state,
      reservationsPageIndex: action.pageEvent.pageIndex,
      reservationsPageSize: action.pageEvent.pageSize
    };
  }),

  on(DriverActions.GetDriverAddressSuccess, (state, action) => {
    return {
      ...state,
      driverAddress: action.address
    };
  }),

  on(DriverActions.GetDeactivationReasonsSuccess, (state, action) => {
    return {
      ...state,
      deactivationReasons: action.deactivationReasons
    };
  }),

  on(DriverActions.GetDriverSuspensionsSuccess, (state, action) => {
    let latestSuspensionEndDate: Date;

    for (const driverSuspension of action.driverSuspensions) {
      if (isNullOrUndefined(driverSuspension.durationHours)) {
        latestSuspensionEndDate = null;
        break;
      } else {
        const endDate: moment = moment(driverSuspension.effectiveAt).add(driverSuspension.durationHours, 'hours');

        if (isNullOrUndefined(latestSuspensionEndDate) || endDate.isAfter(moment(latestSuspensionEndDate))) {
          latestSuspensionEndDate = endDate.toDate();
        }
      }
    }

    return {
      ...state,
      driverSuspensions: action.driverSuspensions,
      lastSuspensionDate: latestSuspensionEndDate
    };
  }),

  on(DriverActions.ViewReferrerSuccess, (state, action) => {
    return {
      ...state,
      viewReferrer: true,
      referrer: action.referrer
    };
  }),

  on(DriverActions.EditReferrer, (state, action) => {
    return {
      ...state,
      editReferrer: true
    };
  }),

  on(DriverActions.SearchForReferrerSuccess, (state, action) => {
    return {
      ...state,
      referrerSearchResults: action.referrerSearchResults
    };
  }),

  on(DriverActions.SelectReferrer, (state, action) => {
    return {
      ...state,
      selectedReferrer: action.selectedReferrer
    };
  }),

  on(DriverActions.SetReferrerSuccess, (state, action) => {
    return {
      ...state,
      referrer: action.referrer,
      editReferrer: false
    };
  }),

  on(DriverActions.ClearReferrerSearch, state => {
    return {
      ...state,
      referrerSearchResults: []
    };
  }),
  on(DriverActions.ShowMoreReferrers, state => {
    return {
      ...state,
      referrerSearchPageSize: state.referrerSearchPageSize + 5
    };
  }),
  on(DriverActions.CancelEditReferrer, state => {
    return {
      ...state,
      editReferrer: false
    };
  }),
  on(DriverActions.GetRandomScreeningYearlyProgressSuccess, (state, action) => {
    return {
      ...state,
      randomScreeningYearlyProgress: action.randomScreeningYearlyProgress
    };
  }),

  on(DriverActions.GetRandomScreeningDriverSelectionInfosSuccess, (state, action) => {
    return {
      ...state,
      randomScreeningDriverSelectionInfos: action.randomScreeningDriverSelectionInfos
    };
  }),

  on(DriverActions.GetRandomScreeningDriverSelectionInfosPagination, (state, action) => {
    return {
      ...state,
      randomScreeningDriverSelectionInfosPageIndex: action.pageEvent.pageIndex,
      randomScreeningDriverSelectionInfosPageSize: action.pageEvent.pageSize
    };
  }),

  on(DriverActions.GetRandomScreeningDriverSelectionSuccess, (state, action) => {
    return {
      ...state,
      randomScreeningDriverSelection: action.randomScreeningDriverSelection
    };
  }),

  on(DriverActions.GetRefusalReasonsSuccess, (state, action) => {
    return {
      ...state,
      refusalReasons: action.refusalReasons
    };
  }),

  on(DriverActions.GetRandomScreeningConfigurationValuesSuccess, (state, action) => {
    return {
      ...state,
      randomScreeningConfigurationValues: action.randomScreeningConfigurationValues
    };
  }),
  on(DriverActions.EditRandomScreeningConfigurationValue, (state, action) => {
    return {
      ...state,
      currentlyEditedRandomScreeningConfigurationValue: state.randomScreeningConfigurationValues.find(
        cv => cv.name === action.ruleValueName
      )
    };
  }),
  on(DriverActions.ConfirmUpdateRandomScreeningConfigurationValue, (state, action) => {
    const clonedCurrentlyEditedRandomScreeningConfigurationValue: RuleValue = cloneDeep(
      state.currentlyEditedRandomScreeningConfigurationValue
    );
    clonedCurrentlyEditedRandomScreeningConfigurationValue.value = action.updatedValue;
    return {
      ...state,
      currentlyEditedRandomScreeningConfigurationValue: clonedCurrentlyEditedRandomScreeningConfigurationValue
    };
  }),

  on(DriverActions.UpdateRandomScreeningConfigurationValueSuccess, (state, action) => {
    const clonedRandomScreeningConfigurationValues: Array<RuleValue> = cloneDeep(state.randomScreeningConfigurationValues);
    const index: number = findIndex(clonedRandomScreeningConfigurationValues, { code: action.ruleValue.code });
    clonedRandomScreeningConfigurationValues.splice(index, 1, action.ruleValue);

    return {
      ...state,
      randomScreeningConfigurationValues: clonedRandomScreeningConfigurationValues,
      currentlyEditedRandomScreeningConfigurationValue: null
    };
  }),

  on(DriverActions.CancelEditRandomScreeningConfigurationValue, (state, action) => {
    return {
      ...state,
      currentlyEditedRandomScreeningConfigurationValue: null
    };
  }),

  on(DriverActions.GetDriverAnnualRideExecutionDataSuccess, (state, action) => {
    return {
      ...state,
      driverAnnualRideExecutionData: action.driverAnnualRideExecutionData
    };
  }),

  on(DriverActions.GetDriverTaxDocumentsSuccess, (state, action) => {
    return {
      ...state,
      driverTaxDocuments: action.taxDocuments
    };
  }),

  on(DriverActions.SelectTabIndex, (state, action) => {
    return {
      ...state,
      selectedTabIndex: action.selectedTabIndex
    };
  })
);
